<template>
    <TransitionRoot as="template" :show="true">
        <Dialog as="div" class="fixed z-50 inset-0 overflow-y-auto" @close="goBack">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0"
                                 enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100"
                                 leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"/>
                </TransitionChild>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300"
                                 enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                 enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
                                 leave-from="opacity-100 translate-y-0 sm:scale-100"
                                 leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div
                        class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-screen-xl sm:w-full sm:p-6">
                        <h2>Create New Payment Link</h2>
                        <div class="lg:flex lg:justify-between gap-8">
                            <div class="mt-8 lg:w-1/3">
                                <Listbox as="div" v-model="link.product">
                                    <ListboxLabel class="block text-sm font-medium text-gray-700">Product </ListboxLabel>
                                    <div class="mt-1 relative">
                                        <ListboxButton class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                            <span class="block truncate" v-if="link.product">
                                                {{ link.product.product.name }}&nbsp;{{ link.product.recurring ? '(' + link.product.nickname + ')' : '' }}
                                            </span>
                                            <span v-else>&nbsp;</span>
                                            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                            </span>
                                        </ListboxButton>

                                        <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                            <ListboxOptions class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                                <ListboxOption as="template" v-for="price in prices" :key="price.id" :value="price" v-slot="{ active, selected }">
                                                    <li :class="[active ? 'text-white bg-indigo-600' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9']">
                                                        <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                                            {{ price.product.name }}&nbsp;{{ price.recurring ? '(' + price.nickname + ')' : '' }}
                                                        </span>
                                                        <span v-if="selected"
                                                              :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                            <CheckIcon class="h-5 w-5" aria-hidden="true"/>
                                                        </span>
                                                    </li>
                                                </ListboxOption>
                                            </ListboxOptions>
                                        </transition>
                                    </div>
                                </Listbox>
                            </div>
                            <div class="mt-8 lg:w-1/3">
                                <Listbox as="div" v-model="link.discount">
                                    <ListboxLabel class="block text-sm font-medium text-gray-700">Discount</ListboxLabel>
                                    <div class="mt-1 relative">
                                        <ListboxButton class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                            <span class="block truncate" v-if="link.discount">
                                                {{ link.discount.name }}
                                            </span>
                                            <span v-else>&nbsp;</span>
                                            <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                                <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                            </span>
                                        </ListboxButton>

                                        <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                            <ListboxOptions class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                                <ListboxOption as="template" v-for="coupon in coupons" :key="coupon.id" :value="coupon" v-slot="{ active, selected }">
                                                    <li :class="[active ? 'text-white bg-indigo-600' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9']">
                                                        <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                                            {{ coupon.name }}
                                                        </span>
                                                        <span v-if="selected"
                                                              :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                            <CheckIcon class="h-5 w-5" aria-hidden="true"/>
                                                        </span>
                                                    </li>
                                                </ListboxOption>
                                            </ListboxOptions>
                                        </transition>
                                    </div>
                                </Listbox>
                            </div>
                            <div class="mt-8 lg:w-1/6">
                                <div class="px-0 mt-3 pt-4 lg:px-6">
                                    <div class="relative flex items-start">
                                        <div class="flex h-6 items-center">
                                            <input id="allows-existing" name="allows-existing" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" v-model="link.allowsExisting" />
                                        </div>
                                        <div class="ml-3 text-sm leading-6">
                                            <label for="allows-existing" class="font-medium text-gray-900">Existing Users?</label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-8 lg:w-1/6">
                                <div class="px-4 mt-1 pt-4 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" @click="saveLink">
                                        <LoadingSpinner :loading="loading" />
                                        Save
                                    </button>
                                    <button type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" @click="goBack" ref="cancelButtonRef">Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {ref, watch, onMounted} from "vue";
import {useRoute, useRouter} from "vue-router"
import {ClipboardDocumentIcon, ChevronUpDownIcon, CheckIcon} from "@heroicons/vue/24/outline";
import {Dialog, DialogOverlay, DialogTitle, Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, TransitionChild, TransitionRoot} from "@headlessui/vue"
import LoadingSpinner from "../../../LoadingSpinner";

export default {
    name: "New",
    components: {
        LoadingSpinner,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        ClipboardDocumentIcon,
        Listbox,
        ListboxButton,
        ListboxLabel,
        ListboxOption,
        ListboxOptions,
        ChevronUpDownIcon,
        CheckIcon,
    },
    setup() {
        const vueRouter = useRouter()
        const vueRoute = useRoute()
        const linkId = vueRoute.params.link
        const hasClipboard = navigator.clipboard !== undefined

        const link = ref({
            price_id: null,
            product: null,
            coupon_id: null,
            discount: null,
            allowsExisting: false,
        })

        const prices = ref([])
        const coupons = ref([])

        const loading = ref(false)

        const goBack = () => {
            vueRouter.back()
        }

        const loadProducts = async () => {
            await axios.get(route('transactions.prices'))
                .then((response) => {
                    prices.value = response.data.data
                })
        }

        const loadCoupons = async (productId) => {
            await axios.get(route('transactions.products.coupons', {productId: productId}))
                .then((response) => {
                    coupons.value = [{id: 0, name: '-- No Discount --'}, ...response.data]
                })
        }

        const copyLink = () => {
            if (hasClipboard) {
                navigator.clipboard.writeText(link.value.link)
            }
        }

        onMounted(() => {
            loadProducts()
        })

        watch(() => link.value.product, (newValue, oldValue) => {
            link.value.price_id = newValue?.id
            loadCoupons(newValue?.product.id)
        }, { deep:true })

        watch(() => link.value.discount, (newValue, oldValue) => {
            link.value.coupon_id = newValue?.id
        }, { deep:true })

        const saveLink = async () => {
            loading.value = true

            await axios.post(route('transactions.links.save'), {
                price_id: link.value.price_id,
                coupon_id: link.value.coupon_id,
                page_data: {
                    allowsExistingUsers: link.value.allowsExisting,
                }
            })
                .then((response) => {
                    loading.value = false
                    goBack()
                })
        }

        return {
            goBack,
            link,
            prices,
            coupons,
            copyLink,
            hasClipboard,
            loading,
            saveLink,
        }
    }
}
</script>
